import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useSeo, SITE_NAME } from '@/lib/seo';
import { Section } from '@/components/layout/Section';
import { Button } from '@/components/ui/button';
import FeaturedImage from '@/components/FeaturedImage';
import GalleryStrip from '@/components/GalleryStrip';
import { useSite, useWhatsappUrl } from '@/context/SiteContext';
import { Leaf, Heart, Sparkles, Check, MessageCircle } from 'lucide-react';

const VALUES = [
    { icon: Leaf, title: 'Stagionalità', desc: 'Scegliamo solo fiori e materiali del momento, perché ogni progetto sia autentico.' },
    { icon: Heart, title: 'Ascolto', desc: 'Ogni storia è unica. Ascoltiamo prima di proporre, sempre.' },
    { icon: Sparkles, title: 'Cura artigianale', desc: 'Gesti lenti, dettagli precisi, materiali nobili. La firma di un atelier vero.' },
];

const SHOP_OFFER = [
    'Bouquet personalizzati',
    'Mazzi di fiori',
    'Fiori recisi freschi',
    'Piante da appartamento',
    'Piante da esterno',
    'Flower box',
    'Decorazioni stagionali',
    'Composizioni commemorative',
    'Marimo',
    'Kokedama',
    'Hydroponiche',
    'Coroncine di laurea',
    'Abbonamenti floreali',
    'Gift card',
];

export default function Atelier() {
    const { t } = useSite();
    const whatsappUrl = useWhatsappUrl();
    useSeo({
        title: `${SITE_NAME} — L'atelier`,
        description: "Floreal Atelier del Fiore a Lomazzo: il nostro negozio, la nostra filosofia. Composizioni su misura, ascolto e cura dei dettagli.",
    });
    return (
        <>
            {/* Hero atelier */}
            <section className="relative pt-32 sm:pt-40 pb-16 sm:pb-24">
                <div className="container-atelier grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-6"
                    >
                        <p className="eyebrow mb-4">{t('atelier.hero.eyebrow', "L'atelier")}</p>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                            {t('atelier.hero.title', 'Un luogo dove i fiori diventano atmosfera.')}
                        </h1>
                        <p className="mt-7 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-prose">
                            {t('atelier.hero.description', "Nel nostro atelier di Via Unione, a Lomazzo, accogliamo chi cerca un fiorista d'autore, ma anche chi vuole semplicemente fermarsi un momento, respirare, scegliere con calma.")}
                        </p>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-6"
                    >
                        <FeaturedImage
                            slot="hero_atelier"
                            alt="Vista dell'atelier Floreal"
                            priority
                            className="rounded-xl aspect-[4/5] w-full shadow-soft"
                            fallbackLabel="L'Atelier"
                        />
                    </motion.div>
                </div>
            </section>

            {/* Cosa trovi da Floreal */}
            <Section id="negozio" className="py-12 sm:py-16 lg:py-20" title={t('atelier.shop.title', 'Cosa trovi da Floreal')}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                    <div className="lg:col-span-5">
                        <FeaturedImage
                            slot="hero_negozio"
                            alt="Composizioni floreali del negozio Floreal"
                            className="rounded-xl aspect-[4/5] w-full shadow-soft"
                            fallbackLabel="Il Negozio"
                        />
                    </div>
                    <div className="lg:col-span-7">
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                            {SHOP_OFFER.map((s) => (
                                <li key={s} className="flex items-start gap-3 text-[15px]">
                                    <span className="h-6 w-6 rounded-full bg-atelier-sage/20 flex items-center justify-center shrink-0 mt-0.5">
                                        <Check className="h-3 w-3 text-atelier-sage-ink" strokeWidth={2.5} />
                                    </span>
                                    {s}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Section>

            {/* Values */}
            <Section eyebrow={t('atelier.values.eyebrow', 'I nostri valori')} title={t('atelier.values.title', 'Una filosofia di lavoro, prima ancora che uno stile.')}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {VALUES.map((v, i) => (
                        <motion.div
                            key={v.title}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                            className="rounded-xl bg-card border border-border p-7"
                        >
                            <div className="h-10 w-10 rounded-md bg-secondary flex items-center justify-center mb-5">
                                <v.icon className="h-5 w-5 text-atelier-bronze-soft" strokeWidth={1.5} />
                            </div>
                            <h3 className="font-serif text-2xl">{v.title}</h3>
                            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* Gallery strip — categoria Atelier */}
            <Section className="py-8 sm:py-12 lg:py-16">
                <GalleryStrip
                    category="atelier"
                    limit={4}
                    tileClass="rounded-lg aspect-square"
                    ariaLabel="Dettagli dell'atelier Floreal"
                />
            </Section>

            {/* CTA finale con WhatsApp */}
            <Section className="py-12 sm:py-16">
                <div className="rounded-xl bg-atelier-ivory border border-atelier-sage/25 px-6 sm:px-10 py-12 sm:py-16 text-center">
                    <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-2xl mx-auto leading-[1.1]">
                        {t('atelier.cta.title', "Vieni a scoprire l'atelier.")}
                    </h3>
                    <p className="mt-4 text-muted-foreground max-w-xl mx-auto leading-relaxed">
                        {t('atelier.cta.description', 'Ti aspettiamo in Via Unione 3 a Lomazzo. Se preferisci, scrivici prima su WhatsApp.')}
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <Button asChild size="lg" className="h-12 px-6">
                            <Link to="/contatti#form">{t('atelier.cta.btn_info', 'Richiedi informazioni')}</Link>
                        </Button>
                        <Button asChild size="lg" variant="outline" className="h-12 px-6 border-foreground/20 bg-background hover:bg-secondary">
                            <a href={whatsappUrl} target="_blank" rel="noreferrer">
                                <MessageCircle className="h-4 w-4 mr-2" /> {t('atelier.cta.btn_whatsapp', 'Scrivici su WhatsApp')}
                            </a>
                        </Button>
                    </div>
                </div>
            </Section>
        </>
    );
}
